import { RequestModel } from "./RequestModel";
import { TechnicianModel } from "./TechnicianModel";
import { ContactModel } from "./ClientModel";
import Helper, { DateFormat } from "@/services/Helper";

class PMModel
{
    constructor(obj?: PMModel)
    {
        if (obj)
        {
            Object.assign(this, obj);

            // Handle date conversions
            if (obj.NextDueDate)
            {
                this._NextDueDate = obj.NextDueDate + "";
            }

            if (obj.LastCompletedDate)
            {
                this._LastCompletedDate = obj.LastCompletedDate + "";
            }

            if (obj.ApprovedDate)
            {
                this._ApprovedDate = obj.ApprovedDate + "";
            }

            if (obj.CompletionDate)
            {
                this._CompletionDate = obj.CompletionDate + "";
            }

            if (obj.CreateDate)
            {
                this._CreateDate = obj.CreateDate + "";
            }

            if (obj.ModifiedDate)
            {
                this._ModifiedDate = obj.ModifiedDate + "";
            }

            // Handle relationships
            if (obj.LinkedRequest)
            {
                this.LinkedRequest = new RequestModel(obj.LinkedRequest);
                this.LinkedRequestId = obj.LinkedRequest.Id;
            }

            if (obj.Technician)
            {
                this.Technician = new TechnicianModel(obj.Technician);
                this.AssignedTechnician = obj.Technician.Id;
            }

            if (obj.Contact)
            {
                this.Contact = new ContactModel(obj.Contact);
                this.Customer = obj.Contact.CustomerNumber;
            }
        }
    }

    // Navigation properties
    public LinkedRequest?: RequestModel | null;
    public Technician?: TechnicianModel | null;
    public Contact?: ContactModel | null;

    // Primary key
    public Id?: number | null;

    // Basic Information
    public Title?: string | null;
    public Description?: string | null;
    public PMType?: string | null;
    public Instructions?: string | null;

    // Relationships
    public Customer?: number | null;
    public AssignedTechnician?: number | null;
    public LinkedRequestId?: number | null;
    public StaffId?: string | null;
    public Location?: string | null;
    public AssetNumber?: string | null;
    public EquipmentName?: string | null;

    // Scheduling
    public FrequencyType?: string | null;
    public FrequencyValue?: number | null;
    
    private _NextDueDate?: string | null;
    public get NextDueDate(): string | null
    {
        return Helper.dateToInput(this.NextDueDateAsDate, DateFormat.Date) || null;
    }
    public set NextDueDate(val: string | null)
    {
        this._NextDueDate = val || null;
    }
    public get NextDueDateAsDate(): Date | null
    {
        if (this._NextDueDate)
        {
            return Helper.dateFromString(this._NextDueDate) || null;
        }
        return null;
    }

    private _LastCompletedDate?: string | null;
    public get LastCompletedDate(): string | null
    {
        return Helper.dateToInput(this.LastCompletedDateAsDate, DateFormat.Date) || null;
    }
    public set LastCompletedDate(val: string | null)
    {
        this._LastCompletedDate = val || null;
    }
    public get LastCompletedDateAsDate(): Date | null
    {
        if (this._LastCompletedDate)
        {
            return Helper.dateFromString(this._LastCompletedDate) || null;
        }
        return null;
    }

    public EstimatedDurationHours?: number | null;

    // Status & Priority
    public PMStatus?: number | null;
    public Priority?: number | null;
    public IsActive: boolean = true;
    public CompletionStatus?: string | null;

    // Tracking & Compliance
    public DaysBeforeDueNotification?: number | null;
    public AllowSkip: boolean = false;
    public RequiresApproval: boolean = false;
    public ApprovedBy?: number | null;
    
    private _ApprovedDate?: string | null;
    public get ApprovedDate(): string | null
    {
        return Helper.dateToInput(this.ApprovedDateAsDate, DateFormat.Date) || null;
    }
    public set ApprovedDate(val: string | null)
    {
        this._ApprovedDate = val || null;
    }
    public get ApprovedDateAsDate(): Date | null
    {
        if (this._ApprovedDate)
        {
            return Helper.dateFromString(this._ApprovedDate) || null;
        }
        return null;
    }

    // Completion Details
    public CompletedBy?: number | null;
    
    private _CompletionDate?: string | null;
    public get CompletionDate(): string | null
    {
        return Helper.dateToInput(this.CompletionDateAsDate, DateFormat.Date) || null;
    }
    public set CompletionDate(val: string | null)
    {
        this._CompletionDate = val || null;
    }
    public get CompletionDateAsDate(): Date | null
    {
        if (this._CompletionDate)
        {
            return Helper.dateFromString(this._CompletionDate) || null;
        }
        return null;
    }

    public CompletionNotes?: string | null;
    public ActualDurationHours?: number | null;

    // Cost Tracking
    public EstimatedCost?: number | null;
    public ActualCost?: number | null;

    // Audit Fields
    private _CreateDate?: string | null;
    public get CreateDate(): string | null
    {
        return Helper.dateToInput(this.CreateDateAsDate, DateFormat.Date) || null;
    }
    public set CreateDate(val: string | null)
    {
        this._CreateDate = val || null;
    }
    public get CreateDateAsDate(): Date | null
    {
        if (this._CreateDate)
        {
            return Helper.dateFromString(this._CreateDate) || null;
        }
        return null;
    }

    public CreatedBy?: number | null;

    private _ModifiedDate?: string | null;
    public get ModifiedDate(): string | null
    {
        return Helper.dateToInput(this.ModifiedDateAsDate, DateFormat.Date) || null;
    }
    public set ModifiedDate(val: string | null)
    {
        this._ModifiedDate = val || null;
    }
    public get ModifiedDateAsDate(): Date | null
    {
        if (this._ModifiedDate)
        {
            return Helper.dateFromString(this._ModifiedDate) || null;
        }
        return null;
    }

    public ModifiedBy?: number | null;

    // Additional Tracking
    public TotalCompletions?: number | null;
    public SkippedCount?: number | null;
    public Tags?: string | null;
    public ServiceReport?: string | null;
    
    public RecCount?: number | null;
}

class PMModelFilter 
{
    public Id?: Array<number> = [];
    public CustomerNumber?: Array<number> = [];
    public AssignedTechnician?: Array<number> = [];
    public Status?: Array<number> = [];
    public Priority?: Array<number> = [];
    public FrequencyType?: string = "";
    public IsActive?: boolean | null = null;
    public CompletionStatus?: string = "";
    public DueDateStart?: Date | null = null;
    public DueDateEnd?: Date | null = null;
    public CompletionDateStart?: Date | null = null;
    public CompletionDateEnd?: Date | null = null;
    public StartDate?: Date | null = null;
    public EndDate?: Date | null = null;
    public IncludeContact?: boolean = false;
    public IncludeTechnician?: boolean = false;
    public IncludeLinkedRequest?: boolean = false;
    public Page?: number = 0;
    public Size?: number = 25;
}

export { PMModel, PMModelFilter }
